import type {NavigateFunction} from "react-router-dom";

export const SESSION_ENDED_PARAM = "session_ended";
export const SESSION_ENDED_REASON_PARAM = "reason";

/** Login-only query keys; must not be carried into authenticated routes. */
export function stripSessionEndedSearchParams(
  searchParams: URLSearchParams,
): URLSearchParams {
  const next = new URLSearchParams(searchParams);
  next.delete(SESSION_ENDED_PARAM);
  next.delete(SESSION_ENDED_REASON_PARAM);
  return next;
}

export function hasSessionEndedSearchParams(search: string): boolean {
  return new URLSearchParams(search).get(SESSION_ENDED_PARAM) === "1";
}

/** HashRouter: query lives in `location.hash` (e.g. `#/login?session_ended=1`). */
export function hasSessionEndedInHash(): boolean {
  const hash = window.location.hash;
  const queryIndex = hash.indexOf("?");
  if (queryIndex === -1) {
    return false;
  }
  return new URLSearchParams(hash.slice(queryIndex + 1)).get(SESSION_ENDED_PARAM) === "1";
}

/**
 * Strip session-ended params from the login URL.
 * Uses string `navigate("/login")` — matches `useSessionEndRedirect` and works with HashRouter.
 */
export function clearSessionEndedLoginSearch(
  navigate: NavigateFunction,
  searchParams?: URLSearchParams,
): void {
  const hasParams =
    searchParams?.get(SESSION_ENDED_PARAM) === "1" || hasSessionEndedInHash();

  if (!hasParams) {
    return;
  }

  if (searchParams != null) {
    const remaining = stripSessionEndedSearchParams(searchParams);
    const qs = remaining.toString();
    navigate(qs ? `/login?${qs}` : "/login", {replace: true});
    return;
  }

  navigate("/login", {replace: true});
}

export function getSessionEndedCopy(reason: string | null | undefined): string {
  switch (reason) {
    case "logged_in_elsewhere":
      return "Signed in on another device.";
    case "user_logout":
      return "You signed out on another device.";
    case "shift_ended":
      return "Your shift ended. Please sign in again.";
    case "session_timeout":
      return "Your session expired. Please sign in again.";
    case "admin_revoked":
      return "Your session was ended by an administrator.";
    case "account_disabled":
      return "Your account is no longer active.";
    case "dispatch_center_disabled":
      return "Your dispatch center is no longer active. Contact your administrator.";
    case "security_policy":
    case "token_limit_reached":
      return "Your session ended. Please sign in again.";
    default:
      return "Your session ended. Please sign in again.";
  }
}
